import {
    AfterViewInit,
    Component,
    ContentChildren, EventEmitter, HostListener, Input, Output, QueryList, TemplateRef, ViewChild, computed, signal
} from "@angular/core";
import {DialogComponent} from "../dialog/dialog.component";
import {WizardDialogStepComponent} from "./wizard-dialog-step/wizard-dialog-step.component";
import {WizardStepStatus} from "@fundamental-ngx/core";
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {UisService} from "../../../service/services/misc/uis/uis.service";
import {DialogService} from "../../../service/services/misc/dialog/dialog.service";
import {firstValueFrom} from "rxjs";
import {
    ConfirmationDialogComponent,
    ConfirmationDialogConfig
} from "../../dialogs/confirmation-dialog/confirmation-dialog.component";

@Component({
    selector: "app-wizard-dialog",
    templateUrl: "./wizard-dialog.component.html",
    styleUrls: ["./wizard-dialog.component.scss"],
    standalone: false
})
export class WizardDialogComponent extends DialogComponent implements AfterViewInit {
    @ContentChildren(WizardDialogStepComponent)
        steps: QueryList<WizardDialogStepComponent>;

    @ViewChild('cancelConfirmationDialog')
        cancelConfirmationRef: TemplateRef<any>;

    @Input() currentIndex: number;
    @Input() isDirty = false;
    @Input() superscriptHeaderText: string;
    @Input() saveButtonTemplate: TemplateRef<any>;
    @Output() beforeStepChange = new EventEmitter<number>();

    isEscapeActive = false;
    showBusyIndicator = false;
    busyIndicatorText = "";

    isNextButtonHidden = false;
    isPrevButtonHidden = true;

    currentStep: WizardDialogStepComponent;
    currentStepSignal = signal<WizardDialogStepComponent | undefined>(undefined);

    nextTitle = computed(() => {
        const step = this.currentStepSignal();
        if (!step?.isValid) {
            const stepId = step?.stepId;
            if (stepId === 'beforeBegin') {
                return "Please confirm the checkbox above before proceeding";
            }
            return "Please complete the required fields";
        }
        return "Next";
    });

    constructor(public override dialogRef: DialogRef, public override uisService: UisService, public dialogService: DialogService) {
        super(dialogRef, uisService);
    }

    @HostListener('document:keydown.escape', ['$event'])
    async onKeydownHandler(_: Event) {
        if (this.isEscapeActive) {
            this.isEscapeActive = false;
            return;
        }
        await this.onCancel();
    }

    ngAfterViewInit() {
        this.currentStep = this.steps?.get(this.currentIndex);
        this.currentStepSignal.set(this.currentStep);
    }

    statusChanged(stepNumber: number, event: WizardStepStatus): void {
        if (event === 'current') {
            this.goToStep(stepNumber);
        }
    }

    goToStep(step: number): void {
        this.scrollTop();
        this.beforeStepChange.emit(step);
        this.currentIndex = step;
        const visibleSteps = this.getVisibleSteps();
        const visibleStepsLength = visibleSteps?.length - 1;
        this.isNextButtonHidden = this.currentIndex >= visibleStepsLength;
        this.isPrevButtonHidden = this.currentIndex === 0;
        this.currentStep = visibleSteps[this.currentIndex];
        this.currentStepSignal.set(this.currentStep);
        this.refreshStepStates(step);
    }

    refreshStepStates(step: number) {
        this.getVisibleSteps()?.forEach((item, index) => {
            if (this.currentIndex > index) {
                item.status = 'completed';
            } else if (step === index) {
                item.status = 'current';
            } else {
                item.status = 'upcoming';
            }
        });
    }

    override async onCancel() {
        if (this.isDirty) {
            const confirmationDialogConfig = new ConfirmationDialogConfig({
                title: "Cancel Wizard Operation",
                bodyText: "Are you sure you want to close the wizard and cancel the current operation? <br/><br/> Any unsaved progress will be lost.",
                cancelText: "Go Back to Wizard",
                confirmText: "Close Wizard"
            });
            this.isEscapeActive = true;
            const dialogRef = this.dialogService.openDialog(ConfirmationDialogComponent, confirmationDialogConfig, undefined, "25rem", {
                right: "0px",
                top: "50%"
            });
            try {
                await firstValueFrom(dialogRef.afterClosed);
            } catch {
                return;
            }
        }
        this.cancel.emit();
        this.dialogRef.dismiss('Cancel');
        this.uisService.sendUISEvent(this.uisService.UIS_EVENT_TYPES.CANCEL_DIALOG + " - " + this.headerText,
            {resource: this.headerText, path: this.dialogRef.data?.resource?.path});
    }

    onPrevClick() {
        this.goToStep(this.currentIndex - 1);
    }

    onNextClick() {
        this.goToStep(this.currentIndex + 1);
    }

    getVisibleSteps() {
        return this.steps?.filter(item => item.isVisible);
    }

    setBusy(isBusy: boolean, text?: string) {
        this.showBusyIndicator = isBusy;
        this.busyIndicatorText = text;
    }

}

